"use client";

import type { FieldPath, FieldPathValue, FieldValues } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "..";
import { TimeField as TimeFieldInput, type TimeFieldProps } from "../../TimeField";
import type { FormFieldProps, Transform } from "../types";

export const TimeField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  description,
  optional,
  className,
  inputClassName,
  transform,
  name,
  control,
  shouldUnregister,
  disabled,
  deps,
  ...timeFieldProps
}: FormFieldProps<TFieldValues, TName, Transform<Date, FieldPathValue<TFieldValues, TName>>> &
  Omit<TimeFieldProps, "value" | "onValueChange" | "invalid"> & {
    /** Classes for the input box itself; `className` styles the surrounding form item. */
    inputClassName?: string;
    /** Fields whose validation reads this one, re-checked when it changes. */
    deps?: FieldPath<TFieldValues> | Array<FieldPath<TFieldValues>>;
  }) => (
  <FormField
    name={name}
    control={control}
    shouldUnregister={shouldUnregister}
    rules={{ deps }}
    render={({ field, fieldState }) => (
      <FormItem className={className}>
        <FormLabel optional={optional}>{label}</FormLabel>
        <FormControl>
          <TimeFieldInput
            aria-label={typeof label === "string" ? label : undefined}
            {...timeFieldProps}
            className={inputClassName}
            disabled={disabled}
            value={transform && field.value != null ? transform.input(field.value) : field.value}
            onValueChange={(value) => {
              if (value) field.onChange(transform ? transform.output(value) : value);
            }}
            invalid={!!fieldState.error}
          />
        </FormControl>
        {!!description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);

TimeField.displayName = "FormTimeField";
